import { UserManager, type User, WebStorageStateStore } from 'oidc-client-ts';
import type { AuthClient, AuthClientConfig, AuthUser, Permission } from './types.js';

/**
 * Thin wrapper over oidc-client-ts that:
 *  - Adapts UserManager events into the @iplan/auth surface.
 *  - Surfaces a permission set parsed from the access-token claims.
 *
 * NOTE: This is the new-stack skeleton. When apps/timesheet's existing
 * AuthToken.ts + useAuthEventHandler.ts are ported in Phase F, their
 * concrete iPlan token-shape parsing replaces parsePermissionsFromUser().
 */
export function createAuthClient(config: AuthClientConfig): AuthClient {
  const store = config.userStore === 'sessionStorage' ? window.sessionStorage : window.localStorage;
  const manager = new UserManager({
    authority: config.authority,
    client_id: config.clientId,
    client_secret: config.clientSecret,
    redirect_uri: config.redirectUri,
    post_logout_redirect_uri: config.postLogoutRedirectUri,
    silent_redirect_uri: config.silentRedirectUri,
    response_type: config.responseType ?? 'code',
    scope: config.scope ?? 'openid profile email',
    // FG (OpenIddict) does not publish a userinfo_endpoint, and the id/access
    // tokens already carry the profile claims (email/name/given_name/role).
    // Default off so signinRedirectCallback doesn't throw "Metadata does not
    // contain property userinfo_endpoint". Override per-client if an IdP that
    // exposes userinfo is ever used.
    loadUserInfo: config.loadUserInfo ?? false,
    automaticSilentRenew: config.automaticSilentRenew ?? false,
    monitorSession: config.monitorSession ?? false,
    userStore: new WebStorageStateStore({ store }),
  });

  const listeners = new Set<(user: AuthUser | null) => void>();

  // Shared in-flight promise for the one-time authorization-code redemption,
  // so duplicate handleCallback() calls (StrictMode / double mount) don't
  // redeem the same code twice. See handleCallback() below.
  let inflightCallback: Promise<AuthUser | null> | null = null;

  const toAuthUser = (user: User | null): AuthUser | null => {
    if (!user) return null;
    return {
      id: user.profile.sub,
      email: user.profile['email'] as string,
      displayName:
        (user.profile['name'] as string | undefined) ??
        (user.profile['email'] as string | undefined) ??
        user.profile.sub,
      permissions: parsePermissionsFromUser(user),
      profile: user.profile,
      access_token: user.access_token ?? null,
      refresh_token: user.refresh_token ?? null,
      raw: user,
    };
  };

  const fire = (authUser: AuthUser | null): void => {
    listeners.forEach((listener) => listener(authUser));
  };

  const tokenExpiredListeners = new Set<() => void>();
  const fireTokenExpired = (): void => {
    tokenExpiredListeners.forEach((listener) => listener());
  };

  manager.events.addUserLoaded((user) => fire(toAuthUser(user)));
  manager.events.addUserUnloaded(() => fire(null));
  manager.events.addAccessTokenExpired(() => {
    fireTokenExpired();
    if (config.automaticSilentRenew ?? false) {
      void manager.signinSilent().catch(() => manager.removeUser());
    } else {
      void manager.removeUser();
    }
  });

  return {
    async signIn() {
      await manager.signinRedirect();
    },
    async signOut() {
      await manager.signoutRedirect();
    },
    handleCallback() {
      // The OIDC authorization code is single-use. React 18 StrictMode
      // double-invokes the effect that drives this in dev, and any double
      // mount/render could call it twice — the second redemption fails with
      // `invalid_grant` ("authorization code has already been redeemed").
      // Share one in-flight promise so the code is exchanged exactly once;
      // reset afterwards so a later, genuinely new sign-in still works.
      if (inflightCallback) return inflightCallback;
      inflightCallback = (async () => {
        const user = await manager.signinRedirectCallback();
        const authUser = toAuthUser(user);
        config.onSigninCallback?.(user);
        return authUser;
      })().finally(() => {
        inflightCallback = null;
      });
      return inflightCallback;
    },
    async getUser() {
      const user = await manager.getUser();
      return toAuthUser(user);
    },
    async getAccessToken() {
      const user = await manager.getUser();
      return user?.access_token ?? null;
    },
    onUserChanged(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    onAccessTokenExpired(listener) {
      tokenExpiredListeners.add(listener);
      return () => tokenExpiredListeners.delete(listener);
    },
  };
}

function parsePermissionsFromUser(user: User): ReadonlySet<Permission> {
  const claim = (user.profile['permissions'] ?? user.profile['perms']) as
    | string
    | string[]
    | undefined;
  if (!claim) return new Set();
  const list = Array.isArray(claim) ? claim : claim.split(/[,\s]+/);
  return new Set(list.filter((s) => s.length > 0));
}
